<template>
  <v-container>
    <v-row class="facility-details" justify="center">
      <v-col>
        <v-row no-gutters>
          <ViewHeader :title="viewTitle" />
        </v-row>
        <v-row no-gutters justify="space-between" class="mt-6">
          <v-col cols="12" class="facility-details__main-data">
            <v-card flat class="facility-details__card">
              <v-alert v-if="isProcessing" type="info">
                {{ $t("message.facilitiesEdit.bulkEditMessage") }}
              </v-alert>
              <v-form @submit.prevent="onFormSubmit" ref="form">
                <div class="d-flex justify-end mb-6">
                  <ui-button
                    color="primary"
                    type="button"
                    outlined
                    width="170px"
                    :disabled="isProcessing"
                    @click="backToFacilitiesList"
                  >
                    {{ $t("message.common.cancel") }}
                  </ui-button>
                  <ui-button
                    color="primary"
                    class="ml-2"
                    type="submit"
                    width="170px"
                    :disabled="isProcessing"
                  >
                    {{ $t("message.common.save") }}
                  </ui-button>
                </div>

                <div class="facility-details__form-content">
                  <p class="border-accent mb-4">
                    {{ $t("message.facilitiesEdit.checkFieldsDescription") }}
                  </p>

                  <InputWithCheckbox
                    :checked.sync="form.facilityChecked"
                    :value.sync="form.facilityName"
                    :label="$t('message.common.facilityName')"
                    :rules="required"
                    noMargin
                    :disabled="!canEditFacilityName"
                    class="max-w-560"
                  >
                    <template #bottom>
                      <InfoBox
                        v-if="canEditFacilityName"
                        class="mt-2"
                        :text="$t('message.gmbVerification.name')"
                        :more-info-link="
                          GMB_VERIFICATION_INFO_LINKS[
                            GMB_CHANGE_FIELD_TYPE.NAME
                          ]($i18n.locale)
                        "
                      />
                    </template>
                  </InputWithCheckbox>

                  <p class="border-accent my-2">
                    {{ $t("message.common.contact") }}
                  </p>

                  <InputWithCheckbox
                    :checked.sync="form.websiteChecked"
                    :value.sync="form.website"
                    :label="$t('message.facilityDetails.forms.websiteAddress')"
                    noMargin
                    :disabled="!canEditFacilityWebsite"
                    class="max-w-560"
                  />

                  <InputWithCheckbox
                    :checked.sync="form.phoneNumber1Checked"
                    :value.sync="form.phoneNumber1"
                    :label="$t('message.facilityDetails.forms.phoneOne')"
                    noMargin
                    :disabled="!canEditFacilityPhoneNumber"
                    class="max-w-560"
                  />
                  <InputWithCheckbox
                    :checked.sync="form.phoneNumber2Checked"
                    :value.sync="form.phoneNumber2"
                    :label="$t('message.facilityDetails.forms.phoneTwo')"
                    noMargin
                    :disabled="!canEditFacilityPhoneNumber"
                    class="max-w-560"
                  />

                  <p class="border-accent my-2">
                    {{ $t("message.common.other") }}
                  </p>

                  <SelectWithCheckbox
                    :checked.sync="form.openStatusChecked"
                    :value.sync="form.openStatus"
                    :items="openStatus"
                    :get-item-name="getOpenStatusLabel"
                    :label="$t('message.common.facilityOpenStatus')"
                    noMargin
                    :disabled="!canEditFacilityOpenStatus"
                    class="max-w-560"
                  />

                  <DatePickerWithCheckbox
                    :checked.sync="form.openingDateChecked"
                    :date.sync="form.openingDate"
                    :label="$t('message.facilityDetails.startDate')"
                    :disabled="!canEditFacilityOpeningDate"
                    class="max-w-560"
                  />

                  <TextAreaWithCheckbox
                    :checked.sync="form.descriptionChecked"
                    :value.sync="form.description"
                    :label="$t('message.common.description')"
                    noMargin
                    :disabled="!canEditFacilityDescription"
                    class="max-w-560"
                  />

                  <v-row>
                    <v-col>
                      <GmbLabelsMassEdit
                        :checked.sync="form.labelsChecked"
                        :labels.sync="form.labels"
                        :type.sync="form.labelsActionType"
                      ></GmbLabelsMassEdit>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col>
                      <GmbUrlAttributesMassEdit
                        :checked.sync="form.urlAttributesChecked"
                        :urlAttributes.sync="form.urlAttributes"
                        :type.sync="form.urlAttributesActionType"
                      ></GmbUrlAttributesMassEdit>
                    </v-col>
                  </v-row>

                  <v-row>
                    <v-col>
                      <GmbAttributesMassEdit
                        :checked.sync="form.attributesChecked"
                        :attributes.sync="form.attributes"
                        :type.sync="form.attributesActionType"
                      ></GmbAttributesMassEdit>
                    </v-col>
                  </v-row>

                  <v-row v-if="showServicesSection">
                    <v-col>
                      <GmbServicesMassEdit
                        :checked.sync="form.servicesChecked"
                        :service.sync="form.services"
                        :type.sync="form.servicesActionType"
                      />
                    </v-col>
                  </v-row>

                  <v-row class="additional-categories">
                    <v-col class="py-0 mb-10">
                      <v-row>
                        <v-col class="d-flex align-center pb-0 max-w-50px">
                          <v-checkbox
                            v-model="additionalCategoriesChecked"
                            :disabled="!canEditFacilityAdditionalCategories"
                          />
                        </v-col>
                        <v-col
                          class="d-flex align-center pb-0"
                          :class="{
                            disabled: !canEditFacilityAdditionalCategories
                          }"
                        >
                          <p class="border-accent mb-0">
                            {{ $t("message.common.additionalCategories") }}
                          </p>
                        </v-col>
                      </v-row>
                      <v-row
                        :class="{
                          disabled: !canEditFacilityAdditionalCategories
                        }"
                      >
                        <v-col class="max-w-50px pb-0" />
                        <v-col class="py-0">
                          <div v-if="additionalCategories.length">
                            <v-chip
                              v-for="item in additionalCategories"
                              :key="item.id"
                              outlined
                              class="mr-2 my-1"
                            >
                              {{ item.name }}
                              <v-icon
                                v-if="canEditFacilityAdditionalCategories"
                                class="ml-2 mr--1"
                                color="grey"
                                @click="
                                  deleteCategory(
                                    CategoryType.ADDITIONAL,
                                    item.id
                                  )
                                "
                              >
                                icon-close
                              </v-icon>
                            </v-chip>
                            <ui-button
                              v-if="
                                additionalCategories.length <
                                  maxAdditionalCategories &&
                                  canEditFacilityAdditionalCategories
                              "
                              color="primary"
                              icon="mdi-plus"
                              class="my-1"
                              outlined
                              @click="
                                openAddCategoryDialog(CategoryType.ADDITIONAL)
                              "
                            >
                              {{
                                $t(
                                  "message.facilityDetails.categories.additional.bulkEditApplyBtnText"
                                )
                              }}
                            </ui-button>
                          </div>
                          <ui-button
                            v-else-if="canEditFacilityAdditionalCategories"
                            color="primary"
                            icon="mdi-plus"
                            class="my-1"
                            outlined
                            @click="
                              openAddCategoryDialog(CategoryType.ADDITIONAL)
                            "
                          >
                            {{
                              $t(
                                "message.facilityDetails.categories.additional.bulkEditApplyBtnText"
                              )
                            }}
                          </ui-button>

                          <v-chip v-else color="grey-darker" class="my-1">
                            {{ $t("message.common.noData") }}
                          </v-chip>
                          <InfoBox
                            v-if="canEditFacilityAdditionalCategories"
                            class="mt-2"
                            :text="$t('message.gmbVerification.category')"
                            :more-info-link="
                              GMB_VERIFICATION_INFO_LINKS[
                                GMB_CHANGE_FIELD_TYPE.CATEGORY
                              ]($i18n.locale)
                            "
                          />
                          <InfoBox
                            v-if="canEditFacilityAdditionalCategories"
                            class="mt-2"
                            :text="
                              $t(
                                'message.facilityDetails.categories.additional.bulkEditTooltip'
                              )
                            "
                          />
                        </v-col>
                      </v-row>

                      <AddCategoryDialog
                        :isOpen="activeDialog === 'addCategory'"
                        :maxWidth="1154"
                        :added-categories-ids="addedCategoriesIds"
                        :chosen-category-type="chosenCategoryType"
                        @add="addCategory($event)"
                        @close="activeDialog = null"
                      />
                    </v-col>
                  </v-row>

                  <v-row class="opening-hours">
                    <v-col class="max-w-50px">
                      <v-checkbox
                        v-model="openingHoursChecked"
                        :disabled="!canEditFacilityHours"
                      />
                    </v-col>
                    <v-col :class="{ disabled: !canEditFacilityHours }">
                      <v-row no-gutters class="mb-10">
                        <v-col cols="12">
                          <div
                            class="d-flex align-center justify-space-between my-4"
                          >
                            <p class="border-accent mb-0">
                              {{ $t("message.facilityDetails.openingHours") }}
                            </p>
                            <v-btn
                              v-if="isOwner && canEditFacilityHours"
                              icon
                              color="primary"
                              @click="openDialog('openingHours')"
                            >
                              <v-icon>
                                icon-edit
                              </v-icon>
                            </v-btn>
                          </div>
                        </v-col>
                        <v-col class="opening-hours__data">
                          <ul class="pl-0">
                            <li
                              v-for="item in openingHours.days"
                              :key="item.id"
                              class="d-flex justify-space-between mb-2"
                            >
                              <div class="mb-2">
                                {{ item.name }}
                              </div>
                              <template v-if="!item.value">
                                <span class="grey--text">
                                  {{ $t("message.common.noData") }}
                                </span>
                              </template>
                              <template v-else>
                                <ul v-if="item.value.length > 0" class="pl-0">
                                  <li
                                    class="text-right"
                                    v-for="(hours, index) in item.value"
                                    :key="`hours-${index}`"
                                  >
                                    {{ formatOpeningHours(hours) }}
                                  </li>
                                </ul>
                                <span class="grey--text" v-else>{{
                                  $t("message.common.closed")
                                }}</span>
                              </template>
                            </li>
                          </ul>
                        </v-col>
                        <v-col cols="12">
                          <InfoBox
                            class="mt-2"
                            :text="
                              $t('message.facilityDetails.openingHoursInfoText')
                            "
                          />
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>

                  <DialogOpeningHours
                    v-if="activeDialog === 'openingHours'"
                    :days="openingHours.days"
                    :item="activeDialogData"
                    :maxWidth="1154"
                    @close="activeDialog = null"
                    @save="saveOpeningHours($event)"
                  />

                  <v-row class="special-hours">
                    <v-col class="max-w-50px">
                      <v-checkbox
                        v-model="specialHoursChecked"
                        :disabled="!canEditFacilityHours"
                      />
                    </v-col>
                    <v-col :class="{ disabled: !canEditFacilityHours }">
                      <v-row no-gutters class="mb-10">
                        <v-col cols="12">
                          <div
                            class="d-flex align-center justify-space-between my-4"
                          >
                            <p class="border-accent mb-0">
                              {{
                                $t("message.facilityDetails.customOpeningHours")
                              }}
                            </p>
                            <v-btn
                              v-if="isOwner && canEditFacilityHours"
                              icon
                              color="primary"
                              @click="openDialog('specialHours')"
                            >
                              <v-icon>
                                icon-edit
                              </v-icon>
                            </v-btn>
                          </div>
                        </v-col>
                        <v-col class="special-hours__data">
                          <template v-if="specialHours.days.length > 0">
                            <ul class="pl-0">
                              <li
                                v-for="item in specialHours.days"
                                :key="item.formatted_date"
                                class="d-flex justify-space-between"
                              >
                                <div class="mb-2">
                                  {{ item.formatted_date }}
                                </div>
                                <ul class="pl-0">
                                  <li
                                    class="text-right mb-2"
                                    v-if="item.is_closed"
                                  >
                                    <span class="grey--text">
                                      {{ $t("message.common.closed") }}
                                    </span>
                                  </li>
                                  <li
                                    class="text-right mb-2"
                                    v-for="(hours, index) in item.value"
                                    :key="`special_hours-${index}`"
                                  >
                                    <span>
                                      {{ formatOpeningHours(hours) }}
                                    </span>
                                  </li>
                                </ul>
                              </li>
                            </ul>
                          </template>
                          <template v-else>
                            <p class="grey--text mb-2">
                              {{ $t("message.common.noData") }}
                            </p>
                          </template>
                        </v-col>
                        <v-col cols="12">
                          <InfoBox
                            class="mt-2"
                            :text="
                              $t(
                                'message.facilityDetails.customOpeningHoursInfoText'
                              )
                            "
                          />
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </div>

                <DialogSpecialHours
                  v-if="activeDialog === 'specialHours'"
                  :days="specialHours.days"
                  :maxWidth="1154"
                  :item="activeDialogData"
                  @close="activeDialog = null"
                  @save="saveSpecialHours($event)"
                />

                <v-row>
                  <v-col>
                    <MoreHoursMassEdit
                      :checked.sync="form.moreHoursChecked"
                      :moreHours.sync="form.moreHours"
                      :type.sync="form.moreHoursActionType"
                    ></MoreHoursMassEdit>
                  </v-col>
                </v-row>
              </v-form>
            </v-card>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import ViewHeader from "@/components/ui/ViewHeader.vue";
import InputWithCheckbox from "@/components/ui/InputWithCheckbox";
import DatePickerWithCheckbox from "@/components/ui/DatePickerWithCheckbox";
import TextAreaWithCheckbox from "../../components/ui/TextAreaWithCheckbox";
import SelectWithCheckbox from "../../components/ui/SelectWithCheckbox";
import DialogOpeningHours from "@/components/FacilityDetails/Dialog/OpeningHoursDialog.vue";
import DialogSpecialHours from "@/components/FacilityDetails/Dialog/SpecialHoursDialog.vue";
import AddCategoryDialog from "@/components/FacilityDetails/Dialog/AddCategoryDialog.vue";
import AlertDialog from "@/components/ui/AlertDialog";
import GmbLabelsMassEdit from "@/components/FacilityDetails/GmbLabels/GmbLabelsMassEdit";
import GmbUrlAttributesMassEdit from "@/components/FacilityDetails/GmbUrlAttributes/UrlAttributesMassEdit";
import GmbAttributesMassEdit from "@/components/FacilityDetails/GmbAttributes/GmbAttributesMassEdit";
import GmbServicesMassEdit from "@/components/FacilityDetails/GmbServices/GmbServicesMassEdit";
import MoreHoursMassEdit from "@/components/FacilityDetails/GmbMoreHours/MoreHoursMassEdit";
import InfoBox from "@/components/ui/InfoBox.vue";

import facilitiesService from "@/services/facilities";
import ErrorHandler from "@/helpers/handlers/error";
import cuid from "cuid";

import { mapKeys, upperFirst } from "lodash";
import { DialogHelper } from "@/helpers/dialog-helper";
import { GmbAttributeValueType } from "@/enums/gmb-attribute-value-type";
import { GmbActionType } from "@/enums/gmb-attribute-action-type";
import { CategoryType } from "@/enums/category-type";
import { permissionsInfo } from "@/helpers/mixins/permissions-info";
import { OPEN_STATUS } from "@/enums/open-status-type";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import gmbUpdateVerification from "@/helpers/mixins/gmb-update-verification";
import { isFeatureFlagEnabled, FEATURE_FLAG } from "@/helpers/posthog";

export default {
  name: "FacilityEditSelected",
  components: {
    InputWithCheckbox,
    DatePickerWithCheckbox,
    TextAreaWithCheckbox,
    SelectWithCheckbox,
    ViewHeader,
    DialogOpeningHours,
    DialogSpecialHours,
    AddCategoryDialog,
    GmbLabelsMassEdit,
    GmbUrlAttributesMassEdit,
    GmbAttributesMassEdit,
    MoreHoursMassEdit,
    InfoBox,
    GmbServicesMassEdit
  },
  mixins: [permissionsInfo, gmbUpdateVerification],
  data() {
    return {
      // GENERAL
      viewTitle: this.$t("message.facilitiesEdit.title"),
      isOwner: true,
      activeDialog: "",
      required: [v => !!v || this.$t("message.error.requiredField")],
      isProcessing: false,
      // MAIN FIELDS
      form: {
        facilityChecked: false,
        facilityName: "",
        websiteChecked: false,
        website: "",
        phoneNumber1Checked: false,
        phoneNumber1: "",
        phoneNumber2Checked: false,
        phoneNumber2: "",
        openStatusChecked: false,
        openStatus: "",
        openingDateChecked: false,
        openingDate: "",
        descriptionChecked: false,
        description: "",
        labelsChecked: false,
        labels: [],
        labelsActionType: null,
        urlAttributesChecked: false,
        urlAttributes: [],
        urlAttributesActionType: null,
        attributesChecked: false,
        attributes: [],
        attributesActionType: null,
        servicesChecked: false,
        services: [],
        servicesActionType: null,
        moreHoursChecked: false,
        moreHours: [],
        moreHoursActionType: null
      },

      // OPENING HOURS
      openingHoursChecked: false,
      openingHours: {
        name: "openingHours",
        title: this.$t("message.facilityDetails.openingHours"),
        days: {
          monday: {
            name: this.$t("message.common.monday"),
            value: [],
            id: cuid()
          },
          tuesday: {
            name: this.$t("message.common.tuesday"),
            value: [],
            id: cuid()
          },
          wednesday: {
            name: this.$t("message.common.wednesday"),
            value: [],
            id: cuid()
          },
          thursday: {
            name: this.$t("message.common.thursday"),
            value: [],
            id: cuid()
          },
          friday: {
            name: this.$t("message.common.friday"),
            value: [],
            id: cuid()
          },
          saturday: {
            name: this.$t("message.common.saturday"),
            value: [],
            id: cuid()
          },
          sunday: {
            name: this.$t("message.common.sunday"),
            value: [],
            id: cuid()
          }
        }
      },
      specialHoursChecked: false,
      specialHours: {
        name: "specialHours",
        title: this.$t("message.facilityDetails.customOpeningHours"),
        days: []
      },

      // OPEN STATUS
      openStatus: [
        OPEN_STATUS.OPEN,
        OPEN_STATUS.CLOSED_PERMANENTLY,
        OPEN_STATUS.CLOSED_TEMPORARILY
      ],

      // CATEGORIES
      CategoryType,
      additionalCategoriesChecked: false,
      maxAdditionalCategories: 9,
      additionalCategories: [],
      chosenCategoryType: null
    };
  },

  beforeRouteEnter(to, from, next) {
    if (!to.params.ids) {
      next({ name: "business-addresses-list" });
    } else {
      next();
    }
  },

  computed: {
    showServicesSection() {
      return isFeatureFlagEnabled(FEATURE_FLAG.CN_5721_SERVICES);
    },
    activeDialogData() {
      return this.$data[this.activeDialog];
    },
    addedCategoriesIds() {
      if (this.chosenCategoryType === CategoryType.PRIMARY) {
        return [this.primaryCategory.id];
      } else if (this.chosenCategoryType === CategoryType.ADDITIONAL) {
        return this.additionalCategories.map(cat => cat.id);
      } else {
        return [];
      }
    }
  },

  methods: {
    openDialog(dialogNumber) {
      this.activeDialog = dialogNumber;
    },

    saveOpeningHours(days) {
      this.openingHours.days = days;
      this.activeDialog = "";
    },

    formatOpeningHours(item) {
      return item.join("-");
    },

    saveSpecialHours(days) {
      this.specialHours.days = days;
      this.activeDialog = "";
    },

    backToFacilitiesList() {
      if (this.$route.query.back) {
        this.$router.push({ path: this.$route.query.back });
      } else {
        this.$router.push({
          name: "business-addresses-list"
        });
      }
    },

    addCategory({ category }) {
      if (this.chosenCategoryType === CategoryType.ADDITIONAL) {
        this.additionalCategories.push(category);
      }

      this.chosenCategoryType = null;
    },

    deleteCategory(type, categoryId) {
      if (type === CategoryType.ADDITIONAL) {
        this.additionalCategories = [
          ...this.additionalCategories.filter(({ id }) => id !== categoryId)
        ];
      }
    },

    async onFormSubmit() {
      if (this.form.facilityChecked) {
        if (!this.$refs.form.validate()) {
          return;
        }
      }

      const body = this.createPayload();

      if (
        Object.keys(body.data).length === 0 &&
        body.data.constructor === Object
      ) {
        DialogHelper.showDialog(
          AlertDialog,
          {
            text: `${this.$t("message.facilityDetails.noChangesInForm")}`,
            buttonText: `${this.$t("message.common.close")}`
          },
          []
        );
        return;
      }

      this.checkGmbVerificationFields(Object.keys(body.data), () =>
        this.save(body)
      );
    },
    getAnalyticsData(payload) {
      let analyticsPayload = JSON.parse(JSON.stringify(payload));

      const services_standard = analyticsPayload.data.service_items
        ? payload.data.service_items.service_items.some(item => item.type === 1)
        : null;
      const custom_services = analyticsPayload.data.service_items
        ? payload.data.service_items.service_items.some(item => item.type === 2)
        : null;

      delete analyticsPayload.data.service_items;

      analyticsPayload.data = {
        ...analyticsPayload.data,
        ...(services_standard && { services_standard }),
        ...(custom_services && { custom_services })
      };

      return analyticsPayload;
    },
    save(payload) {
      DialogHelper.showConfirmDialog(
        {
          text: `${this.$t("message.common.savedDoneChanges")} ${
            this.$route.params.ids.split(",").length
          } ${this.$t("message.common.facilities")}. ${this.$t(
            "message.common.doYouWantToSaveChanges"
          )}`
        },
        async () => {
          const analyticsPayload = this.getAnalyticsData(payload);

          this.$gtm.trackCustomEvent({
            event: GTM_EVENT_NAME.LOCATIONS_MASS_EDIT_SUMMARY,
            num_locations:
              analyticsPayload && analyticsPayload.facilities_ids
                ? analyticsPayload.facilities_ids.length
                : null,
            fields: Object.keys(analyticsPayload.data).join(", ")
          });

          Object.keys(payload.data).forEach(key => {
            if (key == "attributes") {
              const attributesIds = payload.data[key].attributes.map(
                att => att.gmb_attribute.attribute_id
              );

              attributesIds.forEach(id => {
                this.$gtm.trackCustomEvent({
                  event: GTM_EVENT_NAME.LOCATIONS_MASS_EDIT_ATTRIBUTES_DETAILS,
                  num_locations:
                    payload && payload.facilities_ids
                      ? payload.facilities_ids.length
                      : null,
                  attribute_id: id,
                  action:
                    payload &&
                    payload.data &&
                    payload.data.attributes &&
                    payload.data.attributes.type
                      ? Object.keys(GmbActionType).find(
                          key =>
                            GmbActionType[key] === payload.data.attributes.type
                        )
                      : null
                });
              });
            } else if (key == "service_items") {
              this.$gtm.trackCustomEvent({
                event: GTM_EVENT_NAME.SERVICES_BULK_EDIT,
                num_locations:
                  payload && payload.facilities_ids
                    ? payload.facilities_ids.length
                    : null,
                num_services:
                  payload &&
                  payload.data &&
                  payload.data.service_items &&
                  payload.data.service_items.service_items
                    ? payload.data.service_items.service_items.length
                    : null,
                action:
                  payload &&
                  payload.data &&
                  payload.data.service_items &&
                  payload.data.service_items.type
                    ? Object.keys(GmbActionType).find(
                        key =>
                          GmbActionType[key] === payload.data.service_items.type
                      )
                    : null
              });
            } else {
              this.$gtm.trackCustomEvent({
                event: GTM_EVENT_NAME.LOCATIONS_MASS_EDIT_BASIC_DETAILS,
                num_locations:
                  payload && payload.facilities_ids
                    ? payload.facilities_ids.length
                    : null,
                field: key
              });
            }
          });
          // end GTM event stuff
          this.isProcessing = true;
          await facilitiesService
            .patchFacilitiesBulkEdit(payload)
            .then(response => {
              if (typeof response !== "undefined") {
                this.$notify({
                  group: "default",
                  type: "success",
                  text: this.$t("message.notification.changesSaved")
                });
                this.backToFacilitiesList();
              }
            })
            .catch(error => {
              if (
                error &&
                error.response &&
                error.response.data &&
                error.response.data.facilities_ids &&
                error.response.data.facilities_ids.error
              ) {
                this.$notify({
                  group: "default",
                  type: "error",
                  text: error.response.data.facilities_ids.error
                });

                this.$gtm.trackCustomEvent({
                  event: GTM_EVENT_NAME.LOCATION_PROCESSING_IN_PROGRESS,
                  num_locations:
                    payload && payload.facilities_ids
                      ? payload.facilities_ids.length
                      : null,
                  action: "bulk_loc_edit"
                });
              } else if (
                error &&
                error.response &&
                error.response.data &&
                error.response.data.data &&
                error.response.data.data.service_items &&
                error.response.data.data.service_items.service_items
              ) {
                const serviceItems =
                  error.response.data.data.service_items.service_items;
                serviceItems
                  .filter(item => Object.keys(item).length)
                  .forEach(item => {
                    Object.values(item).forEach(errorItem => {
                      this.$notify({
                        group: "default",
                        type: "error",
                        text: errorItem[0]
                      });
                    });
                  });
              } else {
                ErrorHandler.displayError(error);
              }
            })
            .finally(() => {
              this.isProcessing = false;
            });
        }
      );
    },

    createPayload() {
      const body = {
        facilities_ids: this.$route.params.ids.split(","),
        data: {}
      };

      if (this.form.facilityChecked) {
        body.data.name = this.form.facilityName;
      }

      if (this.form.websiteChecked) {
        body.data.website = this.form.website;
      }

      if (this.form.phoneNumber1Checked) {
        body.data.phone_number = this.form.phoneNumber1;
      }

      if (this.form.phoneNumber2Checked) {
        body.data.phone_number_2 = this.form.phoneNumber2;
      }

      if (this.form.openStatusChecked) {
        body.data.open_status = this.form.openStatus;
      }

      if (this.form.openingDateChecked) {
        body.data.open_info = this.form.openingDate;
      }

      if (this.form.descriptionChecked) {
        body.data.description = this.form.description;
      }

      if (this.form.labelsChecked) {
        body.data.labels = {
          labels: this.form.labels,
          type: this.form.labelsActionType
        };
      }

      if (this.form.urlAttributesChecked) {
        body.data.attributes = this.createAttributesPayload(
          this.form.urlAttributes,
          this.form.urlAttributesActionType,
          [GmbAttributeValueType.URL]
        );
      }

      if (this.form.attributesChecked) {
        body.data.attributes = this.createAttributesPayload(
          this.form.attributes,
          this.form.attributesActionType,
          [
            GmbAttributeValueType.BOOL,
            GmbAttributeValueType.ENUM,
            GmbAttributeValueType.REPEATED_ENUM
          ]
        );
      }

      if (this.form.servicesChecked) {
        body.data.service_items = this.createServicesPayload(
          this.form.services,
          this.form.servicesActionType
        );
      }

      if (this.additionalCategoriesChecked) {
        body.data.additional_categories = [
          ...this.additionalCategories.map(({ id }) => id)
        ];
      }

      if (this.openingHoursChecked) {
        const openingHours = {};

        for (let [k, v] of Object.entries(this.openingHours.days)) {
          const upperKey = upperFirst(k);
          if (v.value.length > 0) {
            openingHours[upperKey] = v.value;
          }
        }
        body.data.opening_hours = openingHours;
      }

      if (this.specialHoursChecked) {
        const specialHours = [];

        for (let day of this.specialHours.days) {
          if (day.is_closed) {
            specialHours.push({
              is_closed: day.is_closed,
              start_date: day.start_date
            });
          } else {
            for (let item of day.value) {
              specialHours.push({
                is_closed: day.is_closed,
                close_date: null,
                open_time: item[0],
                close_time: item[1],
                start_date: day.start_date
              });
            }
          }
        }
        body.data.special_hours = specialHours;
      }

      if (this.form.moreHoursChecked) {
        body.data.more_hours = this.createMoreHoursPayload(
          this.form.moreHours,
          this.form.moreHoursActionType
        );
      }

      return body;
    },

    createAttributesPayload(attributes, actionType, valuesTypes) {
      return {
        type: actionType,
        values_types: valuesTypes,
        attributes: attributes.map(item => {
          const attribute = {
            gmb_attribute: {
              attribute_id: item.gmb_attribute.attribute_id
            },
            values: item.values
          };

          if (
            actionType === GmbActionType.DELETE &&
            item.gmb_attribute.value_type === GmbAttributeValueType.ENUM
          ) {
            attribute.values = item.values.map(({ name }) => name);
          }

          return attribute;
        })
      };
    },

    createServicesPayload(service_items, actionType) {
      return {
        type: actionType,
        service_items
      };
    },

    createMoreHoursPayload(moreHours, actionType) {
      return {
        type: actionType,
        more_hours: moreHours.map(hours => {
          return {
            gmb_more_hours: { hours_type_id: hours.type },
            values:
              actionType === GmbActionType.DELETE
                ? []
                : mapKeys(hours.values, function(value, key) {
                    return upperFirst(key);
                  })
          };
        })
      };
    },

    openAddCategoryDialog(type) {
      this.chosenCategoryType = type;
      this.activeDialog = "addCategory";
    },

    getOpenStatusLabel(item) {
      return this.$t(`message.openStatusTypes.${item}`);
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~vuetify/src/styles/settings/_variables.scss";

.v-form p {
  margin-bottom: 0;
  font-weight: 500;
}

.facility-details {
  &__card {
    position: relative;
    padding: $spacing * 5 $spacing * 7.5;
  }

  &__main-data {
    padding-right: $spacing * 2;

    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      padding-right: $spacing * 4;
    }
  }

  &__form-content {
    max-width: 900px;
  }

  &__opening-hours {
    padding-left: $spacing * 2;

    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      padding-left: $spacing * 4;
    }
  }

  &__address-wrapper {
    display: flex;
    flex-direction: column;

    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      flex-direction: row;
    }
  }

  &__map-wrapper {
    margin-bottom: $spacing * 4;
  }

  &__map {
    margin: auto;
    height: 400px;
    width: 100%;
    background-color: $light-4;
    margin-bottom: 20px;

    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      width: 285px;
      height: auto;
      min-height: 285px;
      margin-left: 0;
      margin-right: 0;
    }
  }

  &__map-details {
    width: 100%;

    @media #{map-get($display-breakpoints, 'lg-and-up')} {
      padding-left: $spacing * 4;
    }

    @media #{map-get($display-breakpoints, 'xl-only')} {
      padding-left: $spacing * 7.5;
    }
  }
}

.additional-categories,
.opening-hours,
.special-hours {
  &__data {
    max-width: 486px; // calc(560px - 12px * 2 - 50px)

    li {
      list-style: none;
      @include font-size(14px);
    }
  }
}

.additional-categories {
  border-bottom: 1px solid $light-3;

  .v-chip {
    height: unset;
    max-height: 46px;
    border-radius: 30px;
    @include font-size(14px);
    line-height: 17px;
    padding: $spacing * 1.8125 $spacing * 2.5;
    white-space: normal;
  }
}

.disabled {
  position: relative;
}

.disabled::after {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  background-color: rgba(255, 255, 255, 0.5);
}

.border-accent {
  @include borderAccent($lime);
}

.max-w-560 {
  max-width: 560px;
}

.mr--1 {
  margin-right: $spacing * -0.75;
}
</style>
